export default function BatonRougeTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Baton Rouge Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Baton Rouge travelers actually move helps us anticipate the passport urgencies that arise here. From LSU study-abroad deadlines to petrochemical assignments to state government business trips, the Capital Region operates on a unique rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">BTR — Baton Rouge Metropolitan Airport</h3>
                <p className="text-gray-400 text-xs">North Baton Rouge, about 8 miles from downtown</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              BTR is the primary commercial airport for the Capital Region, offering connections to major domestic hubs including Atlanta, Dallas, Houston, and Charlotte — where Baton Rouge travelers connect to international destinations. Many residents also drive to New Orleans (MSY, about 75 miles) for direct international flights to London, Frankfurt, and Toronto. The airport serves a mix of state government officials, LSU students, petrochemical workers, and business travelers.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking BTR connections through international hubs. Many countries require six months validity beyond your return date — and some petrochemical host countries require even longer validity.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">LSU &amp; Study Abroad Seasons</h3>
                <p className="text-gray-400 text-xs">Louisiana State University, Southern University</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              LSU and Southern University send hundreds of students abroad each year — summer programs in Spain, semester exchanges in Germany, engineering co-ops in Asia, and agricultural research in Latin America. Application deadlines for study-abroad programs often fall in October and February, but many students do not think about their passport until after acceptance. Standard post office processing frequently exceeds program deadlines, making expedited service essential for students who need their documents before orientation.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants (many college students) require an in-person acceptance clerk visit. We guide LSU and Southern students through the exact location, forms, and timeline so they do not miss their program orientation.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-government-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">State Government &amp; Trade Mission Travel</h3>
                <p className="text-gray-400 text-xs">Louisiana State Capitol, agencies, and trade offices</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Louisiana state government officials and employees regularly travel for trade missions, legislative conferences, and international partnerships. The state's economic development office organizes trade delegations to Europe, Asia, and Latin America that often have compressed planning timelines. State employees may receive travel authorization with 2–4 weeks notice, requiring current passports and sometimes visas for the destination country.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> State trade missions often require both valid passports and business visas. Check visa requirements for your destination country as soon as the mission is confirmed — some business visas take 2–4 weeks to process.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-oil-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Petrochemical &amp; Industrial Travel</h3>
                <p className="text-gray-400 text-xs">ExxonMobil, BASF, Dow, and other plants</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Baton Rouge petrochemical corridor — one of the largest in the United States — sends engineers, technicians, and executives to Europe, Asia, and the Middle East for plant inspections, vendor training, and technical negotiations. These assignments often have fixed start dates and compressed timelines, with 2–4 weeks notice being common. Some workers need multiple-entry visas for countries like Saudi Arabia, Qatar, and the UAE, adding complexity to the passport timeline.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Many Middle Eastern countries require work visas with specific validity periods. Coordinate your passport renewal with your visa application to avoid mismatched expiration dates that could invalidate your travel authorization.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-road-map-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">New Orleans Connection</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Many Baton Rouge residents drive to New Orleans (MSY) for direct international flights. If your international trip is booked through MSY, factor in the 75-mile drive and make sure your passport is ready well before your departure date. We can process your expedited passport with the same timeline regardless of which airport you fly from.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}